import {debounce} from '@github/mini-throttle'
// useLocation is safe for files not rendered in a partial on the overview.
// eslint-disable-next-line no-restricted-imports
import {useLocation} from '@github-ui/react-router'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useCallback, useLayoutEffect, useSyncExternalStore} from 'react'

//this is larger than the largest single line can be using firefox's text zoom only feature, so if we detect
//that line height we then check the child because it has the no virtualization wrapper around it
const tooLargeOfALineHeight = 100

//Previously every component that needed the current line height attached its own ResizeObserver to
//`#file-name-id-wide` and read the same DOM node to derive an identical value. On large blobs that meant
//~10 redundant observers and forced-layout reads firing on every resize. We now keep a single shared
//measurement per `lineContainerID` and fan it out to all subscribers via `useSyncExternalStore`.
interface SharedLineHeight {
  height: number
  defaultLineHeight: number
  wrapEnabled: boolean
  observedElement: Element | null
  observer: ResizeObserver | null
  subscribers: Set<() => void>
  //Signature of the last `{location.key, wrapEnabled}` that triggered a measurement, so the ~10 shared
  //consumers re-measuring in their own layout effects only perform one forced-layout read per change.
  lastMeasuredSignature: string | null
}

const sharedLineHeights = new Map<string, SharedLineHeight>()

function getOrCreateSharedLineHeight(key: string, defaultLineHeight: number): SharedLineHeight {
  let shared = sharedLineHeights.get(key)
  if (!shared) {
    shared = {
      height: defaultLineHeight,
      defaultLineHeight,
      wrapEnabled: false,
      observedElement: null,
      observer: null,
      subscribers: new Set(),
      lastMeasuredSignature: null,
    }
    sharedLineHeights.set(key, shared)
  }
  return shared
}

function measure(lineContainerID: string, shared: SharedLineHeight) {
  let detectedLineHeight =
    (document.getElementsByClassName(lineContainerID)[0]?.firstChild as Element)?.getBoundingClientRect().height ??
    shared.defaultLineHeight

  //if the detected line height is way too large, we can assume that it is being wrapped for non virtualization
  //purposes
  if (detectedLineHeight > tooLargeOfALineHeight) {
    detectedLineHeight =
      (document.getElementsByClassName(lineContainerID)[0]?.firstChild?.firstChild as Element)?.getBoundingClientRect()
        .height ?? shared.defaultLineHeight
  }

  //if word wrap is enabled, we don't want to blanket set the height for all lines because it will probably be
  //incorrect for the majority of lines. Defaulting to the initial height lets the useVirtualDynamic actually
  //figure out what the heights should be.
  if (detectedLineHeight !== 0 && detectedLineHeight !== shared.height && !shared.wrapEnabled) {
    shared.height = detectedLineHeight
    for (const notify of shared.subscribers) notify()
  }
}

function subscribe(key: string, defaultLineHeight: number, onStoreChange: () => void) {
  const shared = getOrCreateSharedLineHeight(key, defaultLineHeight)
  shared.subscribers.add(onStoreChange)
  return () => {
    shared.subscribers.delete(onStoreChange)
    if (shared.subscribers.size === 0) {
      shared.observer?.disconnect()
      shared.observer = null
      shared.observedElement = null
      sharedLineHeights.delete(key)
    }
  }
}

function syncObserverAndMeasure(
  key: string,
  lineContainerID: string,
  wrapEnabled: boolean,
  defaultLineHeight: number,
  locationKey: string,
) {
  //`useSyncExternalStore` registers its subscription in a passive effect, which runs *after* this layout
  //effect. For the first consumer of a key that means the entry would not exist yet, so create it here to
  //guarantee the initial measurement and observer are always set up (otherwise a lone consumer would stay
  //stuck at the default height).
  const shared = getOrCreateSharedLineHeight(key, defaultLineHeight)
  shared.wrapEnabled = wrapEnabled

  //apologies for this ridiculous hack, but because in firefox there is an option to zoom ONLY text, and not elements
  //themselves, we needed a piece of text which was always going to be present within the DOM, and the title
  //of the current code blob made sense as the thing for us to use. If someone ever changes the ID of that piece
  //of code, however, this will fail.
  const fileNameText = document.getElementById('file-name-id-wide')
  const observedElementChanged = Boolean(fileNameText) && shared.observedElement !== fileNameText
  if (fileNameText && observedElementChanged) {
    shared.observer?.disconnect()
    const observer = new ResizeObserver(debounce(() => measure(lineContainerID, shared)))
    observer.observe(fileNameText)
    shared.observer = observer
    shared.observedElement = fileNameText
  }

  //navigating to a new file (or toggling wrap) can change the rendered line height without a resize, so re-measure.
  //Every shared consumer runs this effect, so de-dupe on `{location.key, wrapEnabled}` (plus a freshly attached
  //element) to keep the forced-layout read down to once per change instead of once per consumer.
  const signature = `${locationKey}#${wrapEnabled}`
  if (!observedElementChanged && shared.lastMeasuredSignature === signature) return
  shared.lastMeasuredSignature = signature
  measure(lineContainerID, shared)
}

export function useCurrentLineHeight(lineContainerID: string, defaultLineHeight: number = 20) {
  const wrapEnabled = useCodeViewOptions().codeWrappingOption.enabled
  const location = useLocation()

  //All consumers for a given container share a single observer + measurement.
  const key = lineContainerID

  const subscribeCb = useCallback(
    (onStoreChange: () => void) => subscribe(key, defaultLineHeight, onStoreChange),
    [key, defaultLineHeight],
  )
  const getSnapshot = useCallback(
    () => sharedLineHeights.get(key)?.height ?? defaultLineHeight,
    [key, defaultLineHeight],
  )
  const getServerSnapshot = useCallback(() => defaultLineHeight, [defaultLineHeight])

  const lineHeight = useSyncExternalStore(subscribeCb, getSnapshot, getServerSnapshot)

  useLayoutEffect(() => {
    syncObserverAndMeasure(key, lineContainerID, wrapEnabled, defaultLineHeight, location.key)
  }, [key, lineContainerID, wrapEnabled, defaultLineHeight, location.key])

  return lineHeight
}
